import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from 'react-i18next';

const COLORS = {
    likes: '#ff453a',
    comments: '#0a84ff',
    shares: '#30d158',
    views: '#bf5af2',
};

const LABELS = {
    likes: 'Likes',
    comments: 'Comments',
    shares: 'Shares',
    views: 'Views',
};

const formatNum = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return String(n);
};

const formatDateShort = (iso) => {
    const d = new Date(iso);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}`;
};

const ROW_H = 56;
const VB_W = 500;
const SPARK_H = 36;
const SPARK_PAD_T = 4;
const SPARK_PAD_B = 4;
const LABEL_W = 80;
const VAL_W = 70;
const SPARK_W = VB_W - LABEL_W - VAL_W;

const Sparkline = ({ data, metric, color, theme }) => {
    const vals = data.map(d => d[metric] || 0);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max === min ? 1 : max - min;

    const toX = (i) => (i / Math.max(vals.length - 1, 1)) * SPARK_W;
    const toY = (v) => SPARK_PAD_T + (SPARK_H - SPARK_PAD_T - SPARK_PAD_B) - ((v - min) / range) * (SPARK_H - SPARK_PAD_T - SPARK_PAD_B);

    const points = vals.map((v, i) => ({ x: toX(i), y: toY(v), v }));
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

    // Area fill path
    const areaD = pathD + ` L ${points[points.length - 1].x.toFixed(1)} ${SPARK_H} L 0 ${SPARK_H} Z`;

    const current = vals[vals.length - 1];
    const first = vals[0];
    const change = first > 0 ? ((current - first) / first * 100) : 0;
    const changeStr = change >= 0 ? `+${change.toFixed(0)}%` : `${change.toFixed(0)}%`;
    const changeColor = change >= 0 ? '#30d158' : '#ff453a';

    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: theme.border }}>
            {/* Label */}
            <View style={{ width: 88 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                    <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: color }} />
                    <Text style={{ color: theme.textSecondary, fontSize: 12, fontWeight: '500' }}>{LABELS[metric]}</Text>
                </View>
                <Text style={{ color: changeColor, fontSize: 10, marginTop: 1, marginLeft: 12 }}>{changeStr}</Text>
            </View>

            {/* Sparkline */}
            <View style={{ flex: 1 }}>
                <svg width="100%" height={SPARK_H} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none">
                    <defs>
                        <linearGradient id={`grad-${metric}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                        </linearGradient>
                    </defs>
                    <path d={areaD} fill={`url(#grad-${metric})`} />
                    <path d={pathD} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    {/* End dot */}
                    <circle cx={points[points.length-1].x} cy={points[points.length-1].y} r="3" fill={color} />
                </svg>
            </View>

            {/* Current value */}
            <View style={{ width: 64, alignItems: 'flex-end' }}>
                <Text style={{ color: theme.textPrimary, fontSize: 14, fontWeight: '700' }}>{formatNum(current)}</Text>
                <Text style={{ color: theme.textMuted, fontSize: 10 }}>now</Text>
            </View>
        </View>
    );
};

const EngagementChart = ({ data, metric }) => {
    const { theme } = useTheme();
    const { t } = useTranslation();

    if (!data || data.length === 0) {
        return <Text style={{ color: theme.textMuted, fontSize: 13, textAlign: 'center', paddingVertical: 20 }}>{t('app.campaigns.noDataYet')}</Text>;
    }

    const metrics = metric ? [metric] : Object.keys(COLORS).filter(k => data.some(d => (d[k] || 0) > 0));
    if (metrics.length === 0) {
        return <Text style={{ color: theme.textMuted, fontSize: 13, textAlign: 'center', paddingVertical: 20 }}>{t('app.campaigns.noEngagementData')}</Text>;
    }

    // Single snapshot — show stat pills
    if (data.length === 1) {
        const snap = data[0];
        return (
            <View style={{ paddingVertical: 12, alignItems: 'center', gap: 8 }}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 16 }}>
                    {metrics.map((m) => (
                        <View key={m} style={{ alignItems: 'center', gap: 2 }}>
                            <Text style={{ color: COLORS[m], fontSize: 18, fontWeight: '700' }}>{formatNum(snap[m] || 0)}</Text>
                            <Text style={{ color: theme.textMuted, fontSize: 11 }}>{LABELS[m]}</Text>
                        </View>
                    ))}
                </View>
                <Text style={{ color: theme.textMuted, fontSize: 11 }}>Only 1 snapshot — chart builds over time</Text>
            </View>
        );
    }

    // X-axis date range
    const firstDate = formatDateShort(data[0].captured_at);
    const lastDate = formatDateShort(data[data.length - 1].captured_at);

    return (
        <View style={{ width: '100%' }}>
            {metrics.map((m) => (
                <Sparkline key={m} data={data} metric={m} color={COLORS[m]} theme={theme} />
            ))}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
                <Text style={{ color: theme.textMuted, fontSize: 10 }}>{firstDate}</Text>
                <Text style={{ color: theme.textMuted, fontSize: 10 }}>{lastDate}</Text>
            </View>
        </View>
    );
};

export default EngagementChart;
